import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BullseyeQuadrant, BullseyeQuadrantStatement } from "@common/ADAPT.Common.Model/organisation/bullseye-quadrant-statement";
import { ServiceUri } from "@common/configuration/service-uri";
import { map } from "rxjs";
import GenerateBullseyeStatement from "./prompts/GenerateBullseyeStatement.txt";
import GenerateGoalDescription from "./prompts/GenerateGoalDescription.txt";
import GenerateIntegratedVisionSummary from "./prompts/GenerateIntegratedVisionSummary.txt";
import ImproveClarityGrammar from "./prompts/ImproveClarityGrammar.txt";
import IntegrateOwnersVisions from "./prompts/IntegrateOwnersVisions.txt";
import ParaphraseStatement from "./prompts/ParaphraseStatement.txt";
import ReviewIntegratedVisionStatement from "./prompts/ReviewIntegratedVisionStatement.txt";
import ReviewIntegratedVisionSummary from "./prompts/ReviewIntegratedVisionSummary.txt";
import ReviewObjectiveTitle from "./prompts/ReviewObjectiveTitle.txt";
import ReviewVisionStatement from "./prompts/ReviewVisionStatement.txt";
import SuggestObjectiveTitle from "./prompts/SuggestObjectiveTitle.txt";
import SuggestSuccessMeasure from "./prompts/SuggestSuccessMeasure.txt";

interface IChatCompletionBindingModel {
    systemChatMessage: string;
    userChatMessage: string;
}

const DefaultSystemMessage = `You are a helpful assistant that helps people to be more productive and effective in their work. Always respond in a concise manner.
    Reply in plain text. Do not use Markdown or **, # or " in the response.`;
const BaseVisionSystemMessage = `${DefaultSystemMessage}
    In your response, end with the improved vision statement and prepend the improved statement with `;
const ReviewVisionSystemMessage = `${BaseVisionSystemMessage}<br><br><b>Improved vision statement:</b>`;
const SummaryVisionSystemMessage = `${BaseVisionSystemMessage}<b>Summary of integrated visions:</b>`;

@Injectable({
    providedIn: "root",
})
export class ChatAiService {
    private readonly PostCompletionPath = "/PostStatelessChatCompletions";

    public constructor(
        private httpClient: HttpClient,
    ) { }

    public generateBullseyeStatement(statements: BullseyeQuadrantStatement[], maxLength: number) {
        return this.getPromptCompletion({
            systemChatMessage: DefaultSystemMessage,
            userChatMessage: this.processPrompt(GenerateBullseyeStatement, {
                goodAt: statements.filter((st) => st.quadrant === BullseyeQuadrant.GoodAt).map((st) => st.statement).join(", "),
                passionateAbout: statements.filter((st) => st.quadrant === BullseyeQuadrant.PassionateAbout).map((st) => st.statement).join(", "),
                customerNeed: statements.filter((st) => st.quadrant === BullseyeQuadrant.CustomerNeed).map((st) => st.statement).join(", "),
                getPaidFor: statements.filter((st) => st.quadrant === BullseyeQuadrant.GetPaidFor).map((st) => st.statement).join(", "),
                maxLength: maxLength.toString(),
            }),
        } as IChatCompletionBindingModel);
    }

    public generateGoalDescription(goalName: string, currentMeasurement: string, targetMeasurement: string, maxLength = 200) {
        return this.getPromptCompletion({
            systemChatMessage: DefaultSystemMessage,
            userChatMessage: this.processPrompt(GenerateGoalDescription, { goalName, currentMeasurement, targetMeasurement, maxLength: maxLength.toString() }),
        } as IChatCompletionBindingModel);
    }

    public suggestSuccessMeasure(tryToAchieve: string, whyImportantAndNow: string) {
        return this.getPromptCompletion({
            systemChatMessage: DefaultSystemMessage,
            userChatMessage: this.processPrompt(SuggestSuccessMeasure, { tryToAchieve, whyImportantAndNow }),
        } as IChatCompletionBindingModel);
    }

    public suggestObjectiveTitle(objectiveDescription: string) {
        return this.getPromptCompletion({
            systemChatMessage: `${DefaultSystemMessage}. Use present tense and sentence case, not title case.
                    Make it feel excited and make sure it speaks the "why".
                    Ensure the response is strictly less than 100 characters in plain text and do not end the sentence with a period`,
            userChatMessage: this.processPrompt(SuggestObjectiveTitle, { objectiveDescription }),
        } as IChatCompletionBindingModel);
    }

    public reviewObjectiveTitle(title: string, description: string) {
        return this.getPromptCompletion({
            systemChatMessage: DefaultSystemMessage,
            userChatMessage: this.processPrompt(ReviewObjectiveTitle, { title, description }),
        } as IChatCompletionBindingModel);
    }

    public reviewVisionStatement(categoryTitle: string, categoryDescription: string, visionStatement: string) {
        return this.getPromptCompletion({
            systemChatMessage: ReviewVisionSystemMessage,
            userChatMessage: this.processPrompt(ReviewVisionStatement, { categoryTitle, categoryDescription, visionStatement }),
        } as IChatCompletionBindingModel);
    }

    public reviewIntegratedVisionStatement(categoryTitle: string, categoryDescription: string, visionStatements: string[], integratedVision: string) {
        return this.getPromptCompletion({
            systemChatMessage: ReviewVisionSystemMessage,
            userChatMessage: this.processPrompt(ReviewIntegratedVisionStatement, {
                categoryTitle,
                categoryDescription,
                visionStatements: visionStatements.join(", another owner vision: "),
                integratedVision,
            }),
        } as IChatCompletionBindingModel);
    }

    public integrateOwnersVisions(categoryTitle: string, categoryDescription: string, visionStatements: string[]) {
        return this.getPromptCompletion({
            systemChatMessage: `${DefaultSystemMessage}. Prepend the response with "<b>Generated statement:</b>"`,
            userChatMessage: this.processPrompt(IntegrateOwnersVisions, { categoryTitle, categoryDescription, visionStatements: visionStatements.join(", another owner vision: ") }),
        } as IChatCompletionBindingModel);
    }

    public generateIntegratedVisionSummary(integratedStatements: string[]) {
        return this.getPromptCompletion({
            systemChatMessage: SummaryVisionSystemMessage,
            userChatMessage: this.processPrompt(GenerateIntegratedVisionSummary, {
                integratedStatements: integratedStatements.join(","),
            }),
        } as IChatCompletionBindingModel);
    }

    public reviewIntegratedVisionSummary(integratedStatements: string[], summary: string) {
        return this.getPromptCompletion({
            systemChatMessage: ReviewVisionSystemMessage,
            userChatMessage: this.processPrompt(ReviewIntegratedVisionSummary, {
                integratedStatements: integratedStatements.join(","),
                summary,
            }),
        } as IChatCompletionBindingModel);
    }

    public improveClarityGrammar(statement: string) {
        return this.getPromptCompletion({
            systemChatMessage: `${DefaultSystemMessage}. Ending the response with the improved statement prepended with <br><br><b>Improved statement:</b>`,
            userChatMessage: this.processPrompt(ImproveClarityGrammar, { statement }),
        } as IChatCompletionBindingModel);
    }

    public paraphraseStatement(statement: string) {
        return this.getPromptCompletion({
            systemChatMessage: DefaultSystemMessage,
            userChatMessage: this.processPrompt(ParaphraseStatement, { statement }),
        } as IChatCompletionBindingModel);
    }

    private getPromptCompletion(model: IChatCompletionBindingModel) {
        const url = `${ServiceUri.MethodologyServicesServiceBaseUri}${this.PostCompletionPath}`;
        return this.httpClient.post<string>(url, model).pipe(
            map((response) => this.trimLeadingTrailingQuotes(response)),
        );
    }

    // AI generated text sometimes has quotes around them
    private trimLeadingTrailingQuotes(text: string) {
        return text.replace(/^"|"$/g, "");
    }

    private processPrompt(prompt: string, vars: Record<string, string>) {
        return Object.entries(vars).reduce((txt, [key, val]) => txt.replaceAll(`{{${key}}}`, val), prompt);
    }
}
